<template>
    <!-- Navigation above carousel (default) -->
    <ClientOnly v-if="props.displayNav && props.navPosition === 'above'">
        <!-- Teleported navigation (e.g. for multi-experience layout) -->
        <Teleport
            v-if="props.teleportTarget && fullyVisibleSlidesCount < carouselSlides.length"
            :to="props.teleportTarget"
        >
            <div
                role="group"
                :aria-label="$t('product.slider.controlsLabel')"
                class="flex items-center gap-2"
                :class="[{ 'sm:hidden': props.mobileNavOnly }, props.navClass]"
            >
                <button
                    type="button"
                    class="btn btn-circle btn-ghost z-30 border-none"
                    :aria-label="$t('product.slider.prev')"
                    @click="prevSlide()"
                >
                    <MiscIcon name="arrow-left" class="brand-page-color-active:text-brand-page-color" />
                </button>
                <span class="swiper-fraction min-w-[3rem] text-center">{{ currentFraction }}</span>
                <button
                    type="button"
                    class="btn btn-circle btn-ghost z-30 border-none"
                    :aria-label="$t('product.slider.next')"
                    @click="nextSlide()"
                >
                    <MiscIcon name="arrow-right" class="brand-page-color-active:text-brand-page-color" />
                </button>
            </div>
        </Teleport>
        <!-- Inline navigation (default) -->
        <div
            v-else-if="!props.teleportTarget && fullyVisibleSlidesCount < carouselSlides.length"
            ref="navigation"
            role="group"
            :aria-label="$t('product.slider.controlsLabel')"
            class="flex items-center justify-end mb-4 gap-2 order-0"
            :class="[{ 'sm:hidden': props.mobileNavOnly }, props.navClass]"
        >
            <button
                type="button"
                class="btn btn-circle btn-ghost z-10 border-none"
                :aria-label="$t('product.slider.prev')"
                @click="prevSlide()"
            >
                <MiscIcon name="arrow-left" class="brand-page-color-active:text-brand-page-color" />
            </button>

            <span ref="fraction" class="swiper-fraction min-w-[3rem] text-center">{{ currentFraction }}</span>

            <button
                type="button"
                class="btn btn-circle btn-ghost z-10 border-none"
                :aria-label="$t('product.slider.next')"
                @click="nextSlide()"
            >
                <MiscIcon name="arrow-right" class="brand-page-color-active:text-brand-page-color" />
            </button>
        </div>
        <template #fallback>
            <div class="flex items-center justify-end mb-4 gap-2 order-0">
                <MiscSkeleton width="100%" height="44px" />
            </div>
        </template>
    </ClientOnly>

    <MiscCarousel
        ref="baseSlider"
        :slides="carouselSlides"
        :label="$t('product.slider.main')"
        :breakpoints="carouselBreakpoints"
        :gap="props.spaceBetween"
        :enable-arrows="false"
        :enable-dots="props.progressConfig === 'dots'"
        :enable-autoplay="false"
        carousel-width="100%"
        left-arrow-class="btn btn-circle btn-ghost z-30 border-none"
        right-arrow-class="btn btn-circle btn-ghost z-30 border-none"
        dots-container-class="carousel__navdots absolute top-full left-0 right-0 flex justify-center gap-4 mt-2"
        v-bind="$attrs"
        @slide-change="onSlideChange"
    >
        <template #default="{ index }">
            <slot :index="index" />
        </template>
    </MiscCarousel>

    <!-- Progress bar -->
    <div
        v-if="props.progressConfig === 'progress_bar'"
        ref="progressBar"
        class="order-2"
    >
        <div class="swiper-pagination-progressbar swiper-pagination-horizontal rounded-full bg-secondary h-1 block md:relative mt-10 md:top-auto">
            <div
                id="progress-bar"
                class="bg-primary h-full rounded-full transition-all duration-200"
                :style="{'width': progress + '%'}"
            />
        </div>
    </div>

    <!-- Navigation below carousel (e.g. single experience slider) -->
    <ClientOnly v-if="props.displayNav && props.navPosition === 'below'">
        <div
            v-if="fullyVisibleSlidesCount < carouselSlides.length"
            role="group"
            :aria-label="$t('product.slider.controlsLabel')"
            class="flex items-center justify-end mt-0 gap-2"
            :class="[{ 'sm:hidden': props.mobileNavOnly }, props.navClass]"
        >
            <button
                type="button"
                class="btn btn-circle btn-ghost z-30 border-none"
                :aria-label="$t('product.slider.prev')"
                @click="prevSlide()"
            >
                <MiscIcon name="arrow-left" class="brand-page-color-active:text-brand-page-color" />
            </button>
            <span class="swiper-fraction min-w-[3rem] text-center">{{ currentFraction }}</span>
            <button
                type="button"
                class="btn btn-circle btn-ghost z-30 border-none"
                :aria-label="$t('product.slider.next')"
                @click="nextSlide()"
            >
                <MiscIcon name="arrow-right" class="brand-page-color-active:text-brand-page-color" />
            </button>
        </div>
        <template #fallback>
            <div class="flex items-center justify-end mt-6 gap-2">
                <MiscSkeleton width="100%" height="44px" />
            </div>
        </template>
    </ClientOnly>
</template>

<script setup lang="ts">
import MiscCarousel, { type CarouselBreakpoints } from '~/components/misc/MiscCarousel.vue'
import { useBreakpoints } from '@vueuse/core'

// Disable automatic attribute inheritance since we manually handle it with v-bind="$attrs"
defineOptions({
    inheritAttrs: false
})

const baseSlider = ref<InstanceType<typeof MiscCarousel> | null>(null)
const progress = ref(0)
const detectedSlideCount = ref(0)
const currentSlideIndex = ref(0)
const navigation = ref()
const progressBar = ref()

// VueUse breakpoints (Tailwind CSS breakpoints)
const breakpoints = useBreakpoints({
    sm: 640,
    md: 768,
    lg: 1024,
    xl: 1280,
    '2xl': 1536
})

// Resolve each breakpoint ref ONCE, here in setup. `breakpoints.<key>` is a getter
// that calls useMediaQuery() on every property access — reading it from inside a
// computed therefore creates a fresh matchMedia + 'change' listener on every
// re-evaluation, and outside an active effect scope nothing ever disposes them.
const isSm = breakpoints.sm
const isMd = breakpoints.md
const isLg = breakpoints.lg
const isXl = breakpoints.xl
const is2xl = breakpoints['2xl']

interface SlidesPerView {
    xs?: number
    sm?: number
    md?: number
    lg?: number
    xl?: number
    '2xl'?: number
}

interface SliderBaseProps {
    slidesPerView?: SlidesPerView
    mobileGrid?: boolean,
    displayNav?: boolean,
    spaceBetween?: number,
    progressConfig?: string,
    slideCount?: number
    /** CSS selector to teleport the slider navigation into (for external layouts) */
    teleportTarget?: string
    /** Position of the navigation controls relative to the carousel */
    navPosition?: 'above' | 'below'
    /** Show navigation controls only on mobile (below sm breakpoint) */
    mobileNavOnly?: boolean
    /** Additional classes to apply to navigation controls */
    navClass?: string
}

const props = withDefaults(defineProps<SliderBaseProps>(),{
    slidesPerView: () => ({
        xs: 2,
        sm: 3,
        md: 4,
        lg: 5,
        xl: 6,
        '2xl': 6
    }),
    mobileGrid: false,
    displayNav: true,
    spaceBetween: 8,
    progressConfig: 'progress_bar',
    slideCount: 0,
    navPosition: 'above',
    teleportTarget: '',
    mobileNavOnly: false
})

const emit = defineEmits<{
    (event: 'update:teaser-height', data: number): void
}>()

// Convert slots to carousel slides format
const carouselSlides = computed(() => {
    // Use either provided slideCount prop or detected slide count
    const count = props.slideCount || detectedSlideCount.value
    if (count > 0) {
        return Array.from({ length: count }, (_, index) => ({ id: index }))
    }
    return [{ id: 0 }]
})

// Convert slidesPerView format to MiscCarousel breakpoints
const carouselBreakpoints = computed((): CarouselBreakpoints => {
    const slides = props.slidesPerView || {}

    // Start with default configuration
    const defaultConfig = { slidesPerView: slides.xs || 2, slidesToScroll: slides.xs || 2 }
    const breakpoints: CarouselBreakpoints = { default: defaultConfig }

    // Define breakpoint hierarchy for fallback logic
    const breakpointOrder = ['sm', 'md', 'lg', 'xl', '2xl'] as const
    const slidesMap = {
        sm: slides.sm,
        md: slides.md,
        lg: slides.lg,
        xl: slides.xl,
        '2xl': slides['2xl']
    }

    // For each breakpoint, use provided value or fall back to previous breakpoint's value
    let previousConfig = defaultConfig

    for (const bp of breakpointOrder) {
        const value = slidesMap[bp]
        if (value !== undefined) {
            // Use the provided value
            const config = { slidesPerView: value, slidesToScroll: value }
            breakpoints[bp] = config
            previousConfig = config
        } else {
            // Use the current/previous config value (like MiscCarousel does)
            breakpoints[bp] = { ...previousConfig }
        }
    }

    return breakpoints
})

// Get current breakpoint key based on viewport
const currentBreakpointKey = computed(() => {
    if (import.meta.client) {
        if (is2xl.value) return '2xl'
        if (isXl.value) return 'xl'
        if (isLg.value) return 'lg'
        if (isMd.value) return 'md'
        if (isSm.value) return 'sm'
    }
    return 'default'
})

// Get current breakpoint config (now guaranteed to exist due to our fallback logic)
const currentBreakpointConfig = computed(() => {
    const key = currentBreakpointKey.value as keyof CarouselBreakpoints
    return carouselBreakpoints.value[key] || carouselBreakpoints.value.default
})

// Get fully visible slides count (rounded down)
const fullyVisibleSlidesCount = computed(() => {
    return Math.floor(currentBreakpointConfig.value.slidesPerView)
})

// Calculate current fraction display with hydration safety
const currentFraction = computed(() => {
    const totalSlides = props.slideCount || detectedSlideCount.value

    // Prevent hydration mismatches with stable placeholder
    if (!import.meta.client || totalSlides === 0) {
        return '— / —'
    }

    const slidesPerView = fullyVisibleSlidesCount.value
    const visibleSlides = Math.min(currentSlideIndex.value + slidesPerView, totalSlides)

    if (slidesPerView >= totalSlides) {
        return `${totalSlides} / ${totalSlides}`
    }

    return `${visibleSlides} / ${totalSlides}`
})

function prevSlide () {
    baseSlider.value?.previousSlide()
}

function nextSlide () {
    baseSlider.value?.nextSlide()
}

function onSlideChange(index: number) {
    currentSlideIndex.value = index
    updateProgressBar()

    // Emit height update when slide changes
    nextTick(() => {
        getWrapperHeight()
    })
}

function getWrapperHeight () {
    const carouselElement = baseSlider.value?.$el
    if (carouselElement) {
        const slideWrapper = carouselElement.querySelector('.carousel__slides')
        if (slideWrapper) {
            const height = slideWrapper.offsetHeight
            emit('update:teaser-height', height)
        }
    }
}

function updateProgressBar() {
    const totalSlides = props.slideCount || detectedSlideCount.value

    if (totalSlides > 0 && import.meta.client) {
        const slidesPerView = fullyVisibleSlidesCount.value
        const visibleSlides = Math.min(currentSlideIndex.value + slidesPerView, totalSlides)
        progress.value = (visibleSlides / totalSlides) * 100
    }
}

function updateNavigation() {
    const totalSlides = props.slideCount || detectedSlideCount.value

    if (import.meta.client && totalSlides > 0) {
        const slidesPerView = fullyVisibleSlidesCount.value

        if (totalSlides <= slidesPerView) {
            navigation?.value?.classList?.add('hidden')
            progressBar?.value?.classList?.add('hidden')
        } else {
            navigation?.value?.classList?.remove('hidden')
            progressBar?.value?.classList?.remove('hidden')
        }
    }
}

// Initialize slide count from slot content or prop
onMounted(() => {
    nextTick(() => {
        if (props.slideCount) {
            detectedSlideCount.value = props.slideCount
        } else {
            // Count slides from slot content
            const carouselElement = baseSlider.value?.$el
            if (carouselElement) {
                const slides = carouselElement.querySelectorAll('.carousel__slide')
                detectedSlideCount.value = slides.length
            }
        }
        updateNavigation()
        getWrapperHeight()
    })
})

// Watch for slide count changes
watch(() => [detectedSlideCount.value, props.slideCount], () => {
    if (props.slideCount) {
        detectedSlideCount.value = props.slideCount
    }
    updateNavigation()
})
</script>
